import { useCallback, useSyncExternalStore } from "react";
import {
  applyTheme,
  getAppliedTheme,
  getInitialTheme,
  type Theme,
} from "@/lib/theme";

/**
 * Subscribes to the `data-theme` attribute on `<html>`. We treat the DOM
 * attribute itself as the single source of truth — any caller that flips it
 * (the inline script in index.html, the toggle, devtools) propagates to
 * every `useTheme()` consumer through the MutationObserver below.
 */
function subscribe(callback: () => void): () => void {
  if (typeof window === "undefined" || typeof MutationObserver === "undefined") {
    return () => {};
  }
  const observer = new MutationObserver(callback);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["data-theme"],
  });
  return () => observer.disconnect();
}

function getSnapshot(): Theme {
  return getAppliedTheme();
}

function getServerSnapshot(): Theme {
  return getInitialTheme();
}

interface UseThemeResult {
  theme: Theme;
  setTheme: (next: Theme) => void;
  toggle: () => void;
}

export function useTheme(): UseThemeResult {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const setTheme = useCallback((next: Theme) => {
    applyTheme(next);
  }, []);

  const toggle = useCallback(() => {
    applyTheme(getAppliedTheme() === "dark" ? "light" : "dark");
  }, []);

  return { theme, setTheme, toggle };
}
